import {
  fetchRmaTickets,
  useRmaStore,
  RMA_STATUSES,
  convertRmaStatusToLocalStatus,
} from '@/entities/rma';
import { EMITTER_CONSTANTS } from '@/shared/config';
import {
  checkIsChangedSorter,
  convertSortOrder,
  renderSortIcon,
  showMessageError,
  showMessageInfo,
  GETTEXT_ALL_FACILITIES,
  appearDataTable,
  preventAppScroll,
} from '@/shared/lib';
import {
  useEmitter,
  useForceRerender,
  useSetState,
  useUpdateEffect,
} from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { t } from 'ttag';
import { facilitySelection } from '@/entities/facility';
import type { RmaRequestTableProps } from './rmaRequestTable.types';
import { OPTIONS_SEARCH_BY } from '../../config/rmaRequestTable';
import {
  actionIconFormatter,
  cellFormatter,
  convertLocalStatusToRmaStatus,
  dateFormatter,
  deviceCountFormatter,
  formatRmaTickets,
  rmaIdFormatter,
  rmaStatusFormatter,
} from '../../lib/rmaRequestTable';

/**
 * Drives the RMA request table: search, status filter, sorting, cursor paging and live updates.
 * Seeds its filter from the RMA store's request filter, fetches tickets when the tab becomes active
 * or the facility changes, debounces search by 500 ms, and reacts to RMA ticket created and updated
 * socket events by inserting, restatusing or removing rows and refilling the page.
 *
 * @param props - Table props: supportFacilities, selectedFacility and isActive.
 * @returns An object carrying navigate, the filter and status refs, the search input ref, the
 *   table state, and the close, search, status, table-change and column-builder handlers.
 */
const useRmaRequestTable = ({
  supportFacilities,
  selectedFacility,
  isActive,
}: RmaRequestTableProps) => {
  const navigate = useNavigate();

  /**
   * The persisted RMA request list filter read from the RMA Zustand store. Selects requestFilter, which holds the last search value, search-by field and status used on the request tab.
   *
   * @returns The persisted request list filter.
   */
  const rmaRequestGotoRedux = useRmaStore((s) => s.requestFilter);
  /**
   * The RMA store updater that merges changes into the persisted request list filter. Selected from the RMA Zustand store so the request tab can save its search and status selections.
   *
   * @returns The store's updateRequestFilter action.
   */
  const updateRequestFilter = useRmaStore((s) => s.updateRequestFilter);

  const filterObject = useRef<any>({
    status: convertLocalStatusToRmaStatus(rmaRequestGotoRedux.status),
    sortBy: 'requestedAt',
    sortOrder: 'desc',
    rmaIdCursor: undefined,
    requestedAtCursor: undefined,
    returnedDateCursor: undefined,
    rmaId:
      rmaRequestGotoRedux?.searchByValue === 'rmaId' && rmaRequestGotoRedux.searchValue
        ? Number(rmaRequestGotoRedux.searchValue)
        : undefined,
    deviceId:
      rmaRequestGotoRedux?.searchByValue === 'deviceId' && rmaRequestGotoRedux.searchValue
        ? rmaRequestGotoRedux.searchValue
        : undefined,
  });
  const statusList = useRef([
    t`All statuses`,
    RMA_STATUSES.PENDING,
    RMA_STATUSES.APPROVED,
    RMA_STATUSES.REJECTED,
  ]);
  const updateSearchValueTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastQueryFilter = useRef({});
  const searchInputRef = useRef(null);

  const [state, setState] = useSetState({
    searchValue: rmaRequestGotoRedux.searchValue,
    status: rmaRequestGotoRedux.status,
    skeletonData: _.map(_.range(10), (item) => ({
      id: item,
    })),
    data: [] as any[],
    isLoadedData: !(
      facilitySelection.userSupportFacilities()?.length > 0 || supportFacilities?.length > 0
    ),
    page: 1,
    sizePerPage: 10,
    isEndOfData: false,
    isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
    tmpLimit: 0,
    searchByValue: rmaRequestGotoRedux?.searchByValue || OPTIONS_SEARCH_BY[0].value,
    clickedDevices: [] as any[] | null,
    clickedDevicesType: null,
  });

  const forceRerender = useForceRerender();

  /**
   * Opens the details page of an RMA request picked from the list. Navigates to the rma route of the given ticket id.
   *
   * @param id - Identifier of the RMA ticket to open.
   * @returns Nothing.
   */
  const handleClickViewRma = (id: any) => {
    navigate(`/rma/${id}`);
  };

  /**
   * Opens the device popup behind an outbound or inbound device count cell of the RMA request list. The clicked devices and the side they belong to are stored in state.
   *
   * @param devices - Devices listed in the clicked count cell.
   * @param type - Which side the devices belong to, outbound or inbound.
   * @returns Nothing.
   */
  const handleClickDeviceCount = (devices: any, type: any) => {
    setState({
      clickedDevices: devices,
      clickedDevicesType: type,
    });
  };

  /**
   * Closes the RMA request list's device popup. Resets both the clicked devices and their type to null.
   *
   * @returns Nothing.
   */
  const handleClose = () => {
    setState({
      clickedDevices: null,
      clickedDevicesType: null,
    });
  };

  /**
   * Persists the RMA request list's search and status selection into the shared request filter. Values from the argument win, falling back to the search value, status and search-by field currently in state.
   *
   * @param data - Filter values to persist, partially supplied.
   * @returns Nothing.
   */
  const updateSearchRedux = (data: any) => {
    updateRequestFilter({
      searchValue: data?.searchValue || state.searchValue,
      status: data?.status || state.status,
      searchByValue: data?.searchByValue || state.searchByValue,
    });
  };

  /**
   * Loads a page of RMA requests and merges it into the table. The query is scoped to the selected facility and the paging cursors are dropped unless fetching more; a cursor fetch appends the formatted tickets, advances the page and recomputes end-of-data from the returned length and whether any row is new, a cursorless fetch replaces the rows at page one, a superseded query only ends loading and errors empty the table with a toast.
   *
   * @param updatedFilterObject - Filter overrides merged into the current query.
   * @param isFetchMoreByClickingNext - Whether this is a paging fetch that keeps the cursors.
   * @param limit - Row count to request instead of the page size.
   * @returns A promise resolving once the table state has been updated.
   */
  const fetchData = async (
    updatedFilterObject = {},
    isFetchMoreByClickingNext = false,
    limit?: any,
  ) => {
    try {
      filterObject.current.facilityId =
        selectedFacility.value !== GETTEXT_ALL_FACILITIES ? selectedFacility.value : undefined;

      if (!isFetchMoreByClickingNext) {
        _.assign(filterObject.current, {
          rmaIdCursor: undefined,
          requestedAtCursor: undefined,
          returnedDateCursor: undefined,
        });
      }

      const queryFilter = { ...filterObject.current, ...updatedFilterObject };
      filterObject.current = { ...queryFilter };
      lastQueryFilter.current = _.cloneDeep(queryFilter);

      const rmaTickets = await fetchRmaTickets(queryFilter, limit || state.sizePerPage);
      const formattedRmaTicket = formatRmaTickets(rmaTickets);

      if (!_.isEqual(queryFilter, lastQueryFilter.current)) {
        setState({
          isLoadedData: true,
        });
        return;
      }

      const setStateObject = {
        isEndOfData: formattedRmaTicket.length < (limit || state.sizePerPage),
        isLoadedData: true,
        tmpLimit: 0,
      };

      if (
        queryFilter.rmaIdCursor ||
        queryFilter.requestedAtCursor ||
        queryFilter.returnedDateCursor
      ) {
        const diffData = _.differenceBy(formattedRmaTicket, state.data, 'id');
        const appearedData = appearDataTable(state.data, formattedRmaTicket);
        if (limit) {
          _.assign(setStateObject, {
            data: appearedData,
            isEndOfData:
              formattedRmaTicket.length < (limit || state.sizePerPage) || !diffData.length, // *: New data is similar to current data
          });
        } else {
          _.assign(setStateObject, {
            data: appearedData,
            page: isFetchMoreByClickingNext ? state.page + 1 : state.page,
            isEndOfData:
              formattedRmaTicket.length < (limit || state.sizePerPage) || !diffData.length,
          });
        }

        if (_.isEmpty(formattedRmaTicket) && !_.isEmpty(state.data)) {
          showMessageInfo({ content: t`No more requests` });
        }
      } else {
        _.assign(setStateObject, { data: formattedRmaTicket, page: 1 });
      }

      setState(setStateObject);
    } catch (error) {
      showMessageError({ content: error instanceof Error ? error.message : undefined });
      setState({
        data: [],
        isLoadedData: true,
        tmpLimit: 0,
      });
    }
  };

  /**
   * Applies a change to the RMA request list's search inputs. The new value is stored, the search text is additionally cleared when the search-by field changes, and the result is mirrored into the shared request filter.
   *
   * @param name - Search field being changed.
   * @param value - New value of that field.
   * @returns Nothing.
   */
  const onChangeSearchValue = (name: any, value: any) => {
    const objectState = {
      [name]: value,
    };
    if (name === 'searchByValue' && state.searchValue) {
      _.assign(objectState, { searchValue: '' });
    }

    setState(objectState);

    updateSearchRedux(objectState);
  };

  /**
   * Filters the RMA request list by ticket status. The local status label is converted to the API status, the list is refetched, the new status is stored while the table is marked as loading, and the choice is mirrored into the shared request filter.
   *
   * @param name - State key holding the status, matching the control name.
   * @param value - Selected local status label.
   * @returns Nothing.
   */
  const onChangeStatus = (name: any, value: any) => {
    if (value !== state.status) {
      filterObject.current.status = convertLocalStatusToRmaStatus(value);
      fetchData(filterObject.current);
      setState({
        [name]: value,
        isLoadedData: false,
      });
      updateSearchRedux({ status: value });
    }
  };

  /**
   * Reacts to sorting and paging on the RMA request table. A changed sorter rewrites sortBy and sortOrder, clears every cursor and refetches from the start; paging past the loaded rows takes the cursor from the last row of the active sort column, falling back to rmaId when its returned date is null, and fetches only the missing rows, otherwise the page is stored or a no-more-requests notice is shown.
   *
   * @param pagination - Requested page and page size.
   * @param filters - Table filters, unused.
   * @param sorter - Requested sort field and direction.
   * @returns Nothing.
   */
  const handleTableChange = (pagination: any, filters: any, sorter: any) => {
    const isChangedOrder = checkIsChangedSorter(sorter, filterObject.current);

    if (isChangedOrder) {
      const { field, order } = sorter;
      const sortOrder = !order || order === 'ascend' ? 'asc' : 'desc';
      filterObject.current.sortBy = field;
      filterObject.current.sortOrder = sortOrder;
      filterObject.current.rmaIdCursor = undefined;
      filterObject.current.requestedAtCursor = undefined;
      filterObject.current.returnedDateCursor = undefined;
      fetchData(filterObject.current);
      setState({ isLoadedData: false });
      return;
    }

    const { current: page, pageSize: sizePerPage } = pagination;
    if (page * sizePerPage > state.data.length && !state.isEndOfData) {
      filterObject.current.rmaIdCursor =
        filterObject.current.sortBy === 'rmaId'
          ? state.data[state.data.length - 1].rmaId
          : undefined;
      filterObject.current.requestedAtCursor =
        filterObject.current.sortBy === 'requestedAt'
          ? state.data[state.data.length - 1].requestedAt
          : undefined;
      filterObject.current.returnedDateCursor =
        filterObject.current.sortBy === 'returnedDate'
          ? state.data[state.data.length - 1].returnedDate
          : undefined;
      //* In case ticket have null returned date, use rmaId instead
      if (
        filterObject.current.sortBy === 'returnedDate' &&
        !filterObject.current.returnedDateCursor
      ) {
        filterObject.current.rmaIdCursor = state.data[state.data.length - 1].rmaId;
      }
      _.assign(state, {
        page,
        sizePerPage,
      });
      setState({ isLoadedData: false });
      fetchData(filterObject.current, true, sizePerPage * page - state.data.length);
    } else {
      const ceilLength = Math.ceil(state.data.length / sizePerPage) * sizePerPage;
      if (page * sizePerPage > ceilLength && state.isLoadedData) {
        showMessageInfo({ content: t`No more requests` });
      } else {
        setState({
          page,
          sizePerPage,
        });
      }
    }
  };

  /**
   * Builds the column set of the RMA request table. RMA ID, status, outbound and inbound device counts, request date, returned date and the row action are always present while the facility column appears only when facility names are shown; sortable columns get the active sort order and icon, and every cell formatter receives the loading flag plus the device-count or view-ticket handler.
   *
   * @returns Column definitions for the RMA request table.
   */
  const generateColumns = () => [
    {
      dataIndex: 'rmaId',
      title: t`RMA ID`,
      sorter: true,
      sortOrder: convertSortOrder('rmaId', filterObject.current),
      sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
      render: (...props: [any, any, any]) =>
        rmaIdFormatter(...props, {
          isLoadedData: state.isLoadedData,
        }),
      fixed: 'left',
    },
    {
      dataIndex: 'status',
      title: t`Status`,
      width: '9.1%',
      render: (...props: [any, any, any]) =>
        rmaStatusFormatter(...props, {
          isLoadedData: state.isLoadedData,
        }),
    },
    ...(state.isShowFacilityName
      ? [
          {
            dataIndex: 'facility',
            title: t`Facility`,
            width: '30%',
            render: (...props: [any, any, any]) =>
              cellFormatter(...props, {
                isLoadedData: state.isLoadedData,
              }),
          },
        ]
      : []),
    {
      dataIndex: 'outboundDevices',
      title: t`Outbound devices`,
      render: (...props: [any, any, any]) =>
        deviceCountFormatter(...props, {
          isLoadedData: state.isLoadedData,
          type: 'outbound',
          handleClickDeviceCount,
        }),
    },
    {
      dataIndex: 'inboundDevices',
      title: t`Inbound devices`,
      render: (...props: [any, any, any]) =>
        deviceCountFormatter(...props, {
          isLoadedData: state.isLoadedData,
          type: 'inbound',
          handleClickDeviceCount,
        }),
    },
    {
      dataIndex: 'requestedAt',
      title: t`Request date`,
      sorter: true,
      sortOrder: convertSortOrder('requestedAt', filterObject.current),
      sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
      render: (...props: [any, any, any]) =>
        dateFormatter(...props, {
          isLoadedData: state.isLoadedData,
        }),
    },
    {
      dataIndex: 'returnedDate',
      title: t`Returned date`,
      sorter: true,
      sortOrder: convertSortOrder('returnedDate', filterObject.current),
      sortIcon: ({ sortOrder }: any) => renderSortIcon(sortOrder),
      render: (...props: [any, any, any]) =>
        dateFormatter(...props, {
          isLoadedData: state.isLoadedData,
        }),
    },
    {
      dataIndex: 'actionIcon',
      title: t`Actions`,
      onCell: () => ({ 'aria-label': t`Actions` }),
      render: (...props: [any, any, any]) =>
        actionIconFormatter(...props, {
          isLoadedData: state.isLoadedData,
          handleClickViewRma,
        }),
      width: '48px',
      fixed: 'right',
      className: 'action-row',
    },
  ];

  /**
   * Builds the cursor that continues the RMA request list after the last loaded row. The column is chosen from the active sortBy — rmaId, requestedAt or returnedDate — falling back to the rmaId cursor when the last row has no returned date and to an empty object for any other sort.
   *
   * @param data - Rows already loaded into the table.
   * @returns Cursor object for the active sort column.
   */
  const getPagingCursor = (data: any) => {
    if (filterObject.current.sortBy === 'rmaId') {
      return { rmaIdCursor: data.length > 0 ? data[data.length - 1]?.rmaId : undefined };
    }

    if (filterObject.current.sortBy === 'requestedAt') {
      return {
        requestedAtCursor: data.length > 0 ? data[data.length - 1]?.requestedAt : undefined,
      };
    }
    if (filterObject.current.sortBy === 'returnedDate') {
      const cursor = {
        returnedDateCursor: data.length > 0 ? data[data.length - 1]?.returnedDate : undefined,
      };
      if (!cursor.returnedDateCursor) {
        return { rmaIdCursor: data.length > 0 ? data[data.length - 1]?.rmaId : undefined };
      }
      return cursor;
    }
    return {};
  };

  /**
   * Merges a newly arrived RMA ticket into the list without reloading the table. Tickets already present are skipped and, under ascending sort, only appended once the end of the list is reached by adding the paging cursor; the fetched ticket is formatted and unioned by id at the end or the front according to sort order, using a callback update so simultaneous socket messages do not overwrite each other.
   *
   * @param id - Identifier of the RMA ticket to insert.
   * @returns A promise resolving once the list data in state has been extended.
   */
  const addDataToFront = async (id: any) => {
    try {
      if (_.some(state.data, (d) => d.id === id)) return;
      if (filterObject.current.sortOrder === 'asc') {
        if (!state.isEndOfData) {
          return;
        }
        _.assign(filterObject.current, getPagingCursor(state.data));
      }
      const rmaTickets = await fetchRmaTickets({ ...filterObject.current }, 1);
      const formattedRmaTicket = formatRmaTickets(rmaTickets);

      if (_.isEmpty(formattedRmaTicket)) {
        return;
      }

      // Use setState by callback to fix error multi message in socket has been come at the same time;
      setState((prevState) => {
        const currentData = prevState.data;
        let newData = [];
        if (filterObject.current.sortOrder === 'asc') {
          newData = _.unionBy([...currentData, ...formattedRmaTicket], 'id');
        } else {
          newData = _.unionBy([...formattedRmaTicket, ...currentData], 'id');
        }
        return { data: newData };
      });
    } catch (error) {
      console.error(error instanceof Error ? error.message : error, 'Error');
    }
  };

  useEmitter(
    EMITTER_CONSTANTS.RMA_TICKET_UPDATED,
    (msg: any) => {
      if (msg.status === 'New') {
        return;
      }
      /**
       * The loaded request row matching the RMA ticket referenced by an incoming socket message. Located by id within the table's current data.
       *
       * @returns The matching request row, or undefined.
       */
      const el = _.find(state.data, (x) => x.id === msg.id);
      if (el) {
        const ticketUpdateStatus = convertRmaStatusToLocalStatus(msg.status);
        if (state.status === 'All statuses') {
          _.assign(el, { status: ticketUpdateStatus });
          forceRerender();
          return;
        }
        if (ticketUpdateStatus !== state.status) {
          const setStateObject = {};
          const clonedData = _.cloneDeep(state.data);
          _.remove(clonedData, (x) => x.id === msg.id);
          _.assign(setStateObject, { data: clonedData });

          // *: Calculate new page after updating data
          let newPage = state.page;
          if (clonedData.length <= (state.page - 1) * state.sizePerPage) {
            newPage = Math.ceil(clonedData.length / state.sizePerPage);
          }
          if (newPage === 0) {
            newPage = 1;
          }
          _.assign(setStateObject, { page: newPage });

          const cursorObject = getPagingCursor(clonedData);
          /**
           * The names of the cursor fields that actually carry a value. Derived by listing the cursor object's keys and dropping every key whose value is nil.
           *
           * @returns The cursor field names that hold a non-nil value.
           */
          const removeNullCursor = _.filter(
            _.keys(cursorObject),
            (x) => !_.isNil((cursorObject as any)[x]),
          );
          const shouldFetchMore = removeNullCursor.length > 0;
          if (shouldFetchMore) {
            _.assign(setStateObject, {
              tmpLimit: state.data.length - clonedData.length,
            });
          }
          setState(setStateObject);
        }
      }
    },
    [state.data, state.status, state.page, state.sizePerPage],
  );

  useEmitter(
    EMITTER_CONSTANTS.RMA_TICKET_CREATED,
    (msg: any) => {
      if (msg.status !== 'New') {
        return;
      }
      if (
        selectedFacility.value !== GETTEXT_ALL_FACILITIES &&
        msg.facility !== selectedFacility.value
      ) {
        return;
      }
      if (filterObject.current.rmaId && filterObject.current.rmaId !== msg?.rmaId) {
        return;
      }
      addDataToFront(msg.id);
    },
    [state.data, selectedFacility.value],
  );

  useEffect(() => {
    if (isActive && !state.isLoadedData && !state.isEndOfData) {
      fetchData();
    }
  }, [isActive]);

  useUpdateEffect(() => {
    if (supportFacilities?.length > 0 || facilitySelection.userSupportFacilities()?.length > 0) {
      if (isActive) {
        setState({
          isLoadedData: false,
          isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
        });
        fetchData({ deviceIdCursor: undefined });
      } else {
        setState({
          isLoadedData: false,
          isEndOfData: false,
          isShowFacilityName: selectedFacility.value === GETTEXT_ALL_FACILITIES,
        });
      }
    }
  }, [selectedFacility.value]);

  useUpdateEffect(() => {
    if (state.tmpLimit > 0) {
      _.assign(filterObject.current, getPagingCursor(state.data));
      fetchData(filterObject.current, true, state.tmpLimit);
    }
  }, [state.tmpLimit]);

  useUpdateEffect(() => {
    if (updateSearchValueTimeout.current) {
      clearTimeout(updateSearchValueTimeout.current);
      updateSearchValueTimeout.current = null;
    }
    updateSearchValueTimeout.current = setTimeout(() => {
      if (state.searchByValue === 'rmaId') {
        filterObject.current.rmaId = state.searchValue ? Number(state.searchValue) : undefined;
        filterObject.current.deviceId = undefined;
      } else if (state.searchByValue === 'deviceId') {
        filterObject.current.deviceId = state.searchValue || undefined;
        filterObject.current.rmaId = undefined;
      }
      fetchData(filterObject.current);
      setState({
        isLoadedData: false,
      });
    }, 500);
  }, [state.searchValue]);

  useUpdateEffect(() => {
    if (state.isLoadedData) {
      preventAppScroll(false);
    }
  }, [state.isLoadedData]);

  return {
    navigate,
    filterObject,
    statusList,
    searchInputRef,
    state,
    handleClose,
    onChangeSearchValue,
    onChangeStatus,
    handleTableChange,
    generateColumns,
  };
};

export { useRmaRequestTable };
